import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { SymbolGrid } from '../../shell/symbolGrid';
import { IndexerPagedView } from '../../shell/symbolGrid/indexerPagedView';
import { integrationPagedBackend } from '../../shell/symbolGrid/pagedBackend';
import type { BackendClient } from '../../bridge/backend-client';
import type { ViewChangedEvent } from '../../shell/symbolGrid/types';

/*
 * IntegrationValueViewport — the shared engine behind the two OBSERVE-WHILE-WATCHED
 * integration viewports (topic + endpoint). Both are the SAME SymbolGrid the
 * controller symbol page uses, pointed at an integration via IntegrationIndex (a
 * push / non-queryable VALUE source); both stream live window-follow values +
 * sparklines for the on-screen leaves (the api-gateway drives a viewport-scoped
 * observe-lease, so the producer only emits what's visible). They differ on ONE
 * axis — `treeMode` — which the subclass fixes:
 *
 *   • topic    (treeMode = true)  → a hierarchical live namespace browsed on
 *     demand: MQTT/Kafka topics, OPC-UA nodes — roots-first, per-node expand.
 *   • endpoint (treeMode = false) → a FLAT list of named operations, each a live
 *     READ value (REST/GraphQL/gRPC GETs observed at cadence). Discover returns a
 *     flat op set with no hierarchy, so a flat grid is the right shape.
 *
 * Everything else — the value plane, search (the integration index is materialized
 * → FTS works), scale (windowed paging serves million-leaf sources), the row count
 * surfaced to the page badge — is identical, so it lives here once. Each viewport
 * owns its own SymbolGrid lifecycle keyed on the integrationId prop.
 *
 * LIGHT DOM (shares the global symbolGrid.scss + page flex layout) and
 * display:contents so the grid's .vg-host lays out as a direct child of
 * .base-page-content — byte-identical to how controller-detail hosts the grid.
 * The host page renders the active viewport only while the Schema tab is live, so
 * the grid always mounts visible (it mis-measures under display:none). Scroll +
 * shape survive a tab round-trip via the engine's module-level nav-state cache.
 */
export abstract class IntegrationValueViewport extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() integrationId = '';

  /** The one axis the two viewports differ on (see the class doc). */
  protected abstract readonly treeMode: boolean;

  private grid: SymbolGrid | null = null;
  private view: IndexerPagedView | null = null;

  // Surface the grid's row total up to the detail page (the tab/badge count).
  // Listener shape matches the view's SymbolDataListener (`(data?: unknown)`);
  // narrow to the ViewChangedEvent it actually emits.
  private readonly onViewChanged = (data?: unknown): void => {
    const count = (data as ViewChangedEvent | undefined)?.visibleCount ?? 0;
    this.dispatchEvent(new CustomEvent('count-changed', { detail: { count }, bubbles: true, composed: true }));
  };

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM — shares the global symbolGrid.scss + page layout
  }

  connectedCallback(): void {
    super.connectedCallback();
    // display:contents so the grid host lays out as a direct flex child of the
    // page content (the grid measures its host height once at mount). Set on the
    // element itself rather than via a tag-name <style> so the base is reusable.
    this.style.display = 'contents';
  }

  protected firstUpdated(): void {
    this.mountIfReady();
  }

  protected updated(changed: PropertyValues): void {
    // Rebuild fresh for a NEW integration (Lit reuses the element on id change) —
    // but only when the id actually changed FROM a previous value. On the first
    // update cycle `changed` also lists the initial assignment (old value
    // undefined), and firstUpdated has just mounted: tearing down here re-created
    // the grid ~50 ms later as a SECOND view with a second follow session. The
    // first view's lease had already triggered every observe's one-shot first
    // emit, and those values were delivered to the stream we then threw away —
    // the surviving view's stream arrived a few ms too late and painted N/A.
    const rekeyed = (changed.has('integrationId') && changed.get('integrationId') !== undefined)
      || (changed.has('gatewayId') && changed.get('gatewayId') !== undefined);
    if (rekeyed) this.teardownGrid();
    this.mountIfReady(); // also covers a backend that arrived a tick late
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardownGrid();
  }

  private mountIfReady(): void {
    if (this.grid || !this.backend || !this.integrationId) return;
    const host = this.querySelector<HTMLDivElement>('.vg-host') ?? undefined;
    if (!host) return;
    this.view = new IndexerPagedView(
      integrationPagedBackend(this.gatewayId, this.integrationId, this.backend),
      this.treeMode,
    );
    this.view.on('viewChanged', this.onViewChanged);
    // The VIEW owns its toolbar (no backend flags): both value viewports keep the
    // global search box (the integration index is materialized → FTS works) but drop
    // expand/collapse-ALL — a browse-on-demand tree has no full set to expand, and a
    // flat op list has nothing to expand at all.
    this.grid = new SymbolGrid({ container: host, dataSource: this.view, toolbar: { bulkExpand: false } });
    // Integrations are "connected" once their schema is discoverable — there is no
    // per-row connection gate as on a PLC, so mount connected.
    this.grid.mount(true);
  }

  private teardownGrid(): void {
    this.view?.off('viewChanged', this.onViewChanged);
    this.grid?.destroy(); // also destroys the IndexerPagedView (its value port is null for integrations)
    this.grid = null;
    this.view = null;
  }

  /** Chrome rendered ABOVE the grid. Nothing by default, so a viewport that wants
   *  no toolbar pays nothing for the seam. The host is display:contents, so what
   *  this returns becomes a direct flex child of the page's paths wrapper,
   *  sitting above the flex:1 grid host — no extra nesting, and the grid's height
   *  measurement is unaffected. */
  protected renderToolbar(): unknown {
    return nothing;
  }

  protected render() {
    return html`${this.renderToolbar()}<div class="vg-host"></div>`;
  }
}
